import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUser, libelleProfil } from '../variables/constants';
import axiosInstance from '../variables/api/axiosInstance';
import { chargerNavigation, lireNavigationEnCache } from '../services/navigationService';
import CBConnectLogo from './CBConnectLogo';
import badgeCarteBrune from '../assets/images/logo.png';
import NotificationBell from './NotificationBell';
import { I, C } from './sidebar/icones';
import Section from './sidebar/Section';
import { lireSectionsOuvertes, ecrireSectionsOuvertes, lireModeCompact, ecrireModeCompact } from '../utils/preferencesSidebar';
import { sectionsParDefaut, fusionnerSectionsOuvertes, basculerSectionExclusive } from '../utils/sectionsOuvertes';
import { correspond, cheminActifMenu } from '../utils/menuActif';

/* ── Persistance entre remontages (la SideBar est remontée à chaque navigation) ── */
let persistedOpenKeys = null;   // null = jamais initialisé ; [] = volontairement tout replié
let persistedScroll = 0;
// Sections du dernier chemin connu, ou null si aucune navigation connue (premier
// montage, rechargement de page) : sert a distinguer une navigation d'un remontage.
let persistedSectionsPrecedentes = null;

// Clés des groupes (items avec sous-menu) contenant le chemin actif.
// Le payload de /v1/navigation est plat au niveau racine : pas de { section, items }.
const groupesPourChemin = (menuCourant, pathname) =>
  menuCourant
    .filter((it) => it.children?.some((ch) => correspond(pathname, ch.path)))
    .map((it) => it.cle);

export default function SideBar() {
  const { t } = useTranslation('commun');
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  // Menu servi par /v1/navigation, déjà filtré selon les habilitations de l'appelant.
  const [menu, setMenu] = useState(() => lireNavigationEnCache() ?? []);
  const [erreurMenu, setErreurMenu] = useState(false);
  const [chargementMenu, setChargementMenu] = useState(!lireNavigationEnCache());
  // Restaure l'état déplié précédent, sinon ouvre le groupe du chemin actif.
  const [openKeys, setOpenKeys] = useState(
    () => persistedOpenKeys ?? groupesPourChemin(lireNavigationEnCache() ?? [], location.pathname),
  );
  // null = l'utilisateur n'a jamais choisi : on appliquera le defaut au premier
  // rendu ou le menu est disponible (voir l'effet ci-dessous).
  const [sectionsOuvertes, setSectionsOuvertes] = useState(lireSectionsOuvertes);

  // N'ecrit pas la preference : l'effet de persistance ci-dessous est le seul
  // point d'ecriture, pour que la bascule manuelle et l'ouverture automatique a
  // la navigation soient enregistrees de la meme facon.
  const basculerSection = useCallback((titre) => {
    setSectionsOuvertes((prev) => basculerSectionExclusive(prev, titre));
  }, []);
  const [nonLusMail, setNonLusMail] = useState(0);
  const [nonLusConversations, setNonLusConversations] = useState(0);
  const [demandesAppareils, setDemandesAppareils] = useState(0);
  const [declarationsCorbeille, setDeclarationsCorbeille] = useState(0);
  const user = getUser();

  const [compact, setCompact] = useState(lireModeCompact);

  // Pilote la largeur reelle : la sidebar, la NavBar et le Layout lisent tous
  // --cb-sidebar-w. Pose sur <html> pour etre visible de tout l'arbre.
  // useLayoutEffect (et non useEffect) : la variable doit etre posee AVANT le
  // premier paint. Sinon, au rechargement en mode compact deja actif, le
  // premier rendu se fait a 250px puis bascule a 64px apres coup - l'aside
  // saute instantanement pendant que NavBar/Layout, eux, transitionnent sur
  // 220 ms, ce qui recree exactement le trou que le mode compact devait
  // supprimer.
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--cb-sidebar-w',
      compact ? 'var(--cb-sidebar-w-sm)' : 'var(--cb-sidebar-w-lg)',
    );
  }, [compact]);

  const basculerCompact = useCallback(() => {
    setCompact((prev) => {
      ecrireModeCompact(!prev);
      return !prev;
    });
  }, []);

  const rechargerMenu = useCallback(async () => {
    setErreurMenu(false);
    setChargementMenu(true);
    try {
      setMenu(await chargerNavigation());
    } catch {
      // On garde le cache s'il existe ; sinon on signale l'echec.
      if (!lireNavigationEnCache()) setErreurMenu(true);
    } finally {
      setChargementMenu(false);
    }
  }, []);

  useEffect(() => { rechargerMenu(); }, [rechargerMenu]);

  // Mémorise l'état déplié pour le prochain montage (après navigation).
  useEffect(() => { persistedOpenKeys = openKeys; }, [openKeys]);

  /**
   * Ouvre le groupe qui contient la route courante - et lui seul.
   *
   * La fusion precedente (`new Set([...prev, ...requis])`) accumulait : naviguer
   * de « Etats statistiques » vers « Import historique » laissait les deux
   * deplies. C'est le meme accordeon exclusif que `toggleKey`, applique cette
   * fois a la navigation plutot qu'au clic.
   *
   * Aucun groupe requis (route hors menu, ou entree sans parent) : on ne touche
   * a rien, sinon arriver sur « Instruire un dossier » refermerait le groupe que
   * l'utilisateur venait d'ouvrir a la main.
   */
  useEffect(() => {
    const requis = groupesPourChemin(menu, location.pathname);
    if (!requis.length) return;
    setOpenKeys((prev) => (
      prev.length === requis.length && requis.every((k) => prev.includes(k))
        ? prev          // identite referentielle : evite une boucle de rendu
        : requis
    ));
  }, [menu, location.pathname]);

  // Defaut au premier chargement, puis ouverture automatique de la section
  // visitee uniquement quand on change reellement de section (pas quand on
  // navigue a l'interieur de la meme section, pour respecter un repli manuel).
  useEffect(() => {
    if (!menu.length) return;
    const requises = sectionsParDefaut(menu, location.pathname);
    // On capture la valeur AVANT de la muter, et on mute AVANT d'appeler
    // setState : sinon la correction reposerait sur le fait que React execute
    // l'updater de facon synchrone - un detail interne, non garanti par l'API.
    // S'il devenait differe, l'updater lirait la nouvelle valeur, comparerait
    // les sections a elles-memes, et l'ouverture automatique ne se
    // declencherait plus jamais.
    const precedentes = persistedSectionsPrecedentes;
    persistedSectionsPrecedentes = requises;
    setSectionsOuvertes((prev) => fusionnerSectionsOuvertes(prev, requises, precedentes));
  }, [menu, location.pathname]);

  // Seul point d'ecriture de la preference. Un effet et non un appel dans les
  // updaters : ceux-ci peuvent etre invoques deux fois en mode strict, et
  // l'ouverture automatique ci-dessus doit etre persistee autant que la bascule
  // manuelle - sans quoi la preference derive de la section reellement ouverte
  // et un rechargement rouvre la mauvaise.
  useEffect(() => {
    if (sectionsOuvertes !== null) ecrireSectionsOuvertes(sectionsOuvertes);
  }, [sectionsOuvertes]);

  // Restaure la position de défilement de la barre après remontage.
  useLayoutEffect(() => {
    if (navRef.current) navRef.current.scrollTop = persistedScroll;
  }, []);

  // Badge messagerie - rafraîchi toutes les 60s
  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await axiosInstance.get('/v1/messagerie/inbox/non-lus');
        setNonLusMail(r.data?.data ?? 0);
      } catch { /* silencieux si non configuré */ }
    };
    fetch();
    const id = setInterval(fetch, 60000);
    return () => clearInterval(id);
  }, []);

  // Badge conversations internes - sondage toutes les 60 s, et mise à jour
  // immédiate par l'événement que la page Conversations émet (temps réel).
  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await axiosInstance.get('/v1/conversations/non-lus', { skipErrorToast: true });
        setNonLusConversations(r.data?.data ?? 0);
      } catch { /* silencieux sans habilitation */ }
    };
    fetch();
    const id = setInterval(fetch, 60000);
    const surEvenement = (e) => setNonLusConversations(e.detail ?? 0);
    window.addEventListener('cb:conversations-non-lus', surEvenement);
    return () => { clearInterval(id); window.removeEventListener('cb:conversations-non-lus', surEvenement); };
  }, []);

  // Badge « Appareils » - nombre de demandes de validation en attente.
  // Sondage à 60 s comme les autres : une demande qui attend une minute de plus
  // ne change rien, alors qu'un flux temps réel pour trois lignes par semaine
  // coûterait une connexion permanente. Silencieux sans l'habilitation
  // SECURITE_APPAREILS_LIRE - la grande majorité des profils n'a pas à voir ce
  // compteur, et un toast d'erreur à chaque sondage serait insupportable.
  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await axiosInstance.get('/v1/admin/securite/demandes/en-attente/compteur',
          { skipErrorToast: true });
        setDemandesAppareils(r.data?.data ?? 0);
      } catch { /* silencieux sans habilitation */ }
    };
    fetch();
    const id = setInterval(fetch, 60000);
    const surEvenement = (e) => setDemandesAppareils(e.detail ?? 0);
    window.addEventListener('cb:demandes-appareils', surEvenement);
    return () => { clearInterval(id); window.removeEventListener('cb:demandes-appareils', surEvenement); };
  }, []);

  // Declarations en attente d'affectation. Meme dispositif que ci-dessus, et pour
  // la meme raison : sans ce compteur, les affectations en attente sont une piece
  // ou il faut penser a entrer - c'est-a-dire une piece ou l'on n'entre pas.
  // Silencieux sans l'habilitation SINISTRE_AUTO_AFFECTATION.
  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await axiosInstance.get('/v1/sinistres/corbeille/compteur', { skipErrorToast: true });
        setDeclarationsCorbeille(r.data?.data ?? 0);
      } catch { /* silencieux sans habilitation */ }
    };
    fetch();
    const id = setInterval(fetch, 60000);
    const surEvenement = (e) => setDeclarationsCorbeille(e.detail ?? 0);
    window.addEventListener('cb:declarations-corbeille', surEvenement);
    return () => { clearInterval(id); window.removeEventListener('cb:declarations-corbeille', surEvenement); };
  }, []);

  // Une seule entrée allumée : la plus spécifique. Un simple test de préfixe
  // allumait aussi les entrées parentes lorsque deux chemins s'imbriquent
  // (« Attestations digitales » restait allumée sur « Commandes digitales »).
  const cheminActif = cheminActifMenu(menu, location.pathname);
  const isActive = (path) => !!path && path === cheminActif;

  /**
   * Depliage d'un groupe (« Etats statistiques », « Import historique »…).
   *
   * ACCORDEON EXCLUSIF, comme les sections : ouvrir un groupe referme celui qui
   * l'etait. L'implementation precedente ajoutait a la liste - deux groupes de
   * neuf et quatre enfants pouvaient rester deplies en meme temps, et la barre
   * defilait sur deux ecrans sans qu'on sache d'ou l'on venait.
   *
   * Refermer le seul groupe ouvert laisse `[]`, un etat legitime : l'utilisateur
   * a le droit de tout replier.
   */
  const toggleKey = (k) =>
    setOpenKeys((prev) => (prev.includes(k) ? [] : [k]));

  const initials = user ? `${(user.prenom || user.prenoms || 'A')[0]}${(user.nom || 'D')[0]}`.toUpperCase() : 'AD';
  const username = user ? `${user.prenoms || user.prenom || ''} ${user.nom || ''}`.trim() : t('sideBar.administrateur');

  return (
    <aside
      ref={navRef}
      onScroll={(e) => { persistedScroll = e.currentTarget.scrollTop; }}
      style={{
        position: 'fixed', top: 'var(--cb-banniere-h, 0px)', left: 0, bottom: 0,
        width: 'var(--cb-sidebar-w, 250px)', zIndex: 1000,
        background: C.bg1, display: 'flex', flexDirection: 'column',
        overflowY: 'auto', overflowX: 'hidden',
      }}>
      {/* ── Logo ── */}
      <div style={{
        padding: compact ? '18px 8px 16px' : '18px 16px 16px',
        borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
      }}>
        {/* Badge Carte Brune, sans le cadre-boussole : a cette taille il
            reduisait le sceau au quart de la boite, illisible. Sans cadre, le
            badge occupe toute la place - et il porte deja son propre anneau. */}
        <CBConnectLogo
          size={compact ? 36 : 52}
          variant="white"
          showLabel={false}
          cadre={false}
          embleme={badgeCarteBrune}
        />
        {!compact && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>CBConnect</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>BNCB Togo</div>
          </div>
        )}
        <button
          onClick={basculerCompact}
          aria-label={compact ? t('sideBar.deployerMenu') : t('sideBar.reduireMenu')}
          title={compact ? t('sideBar.deployerMenu') : t('sideBar.reduireMenu')}
          style={{
            background: 'transparent', border: 'none', color: C.textSec,
            cursor: 'pointer', padding: 4, display: 'flex',
            transform: compact ? 'rotate(-90deg)' : 'rotate(90deg)',
          }}
        >
          <I.Chevron />
        </button>
      </div>

      {/* ── Navigation ── */}
      <nav style={{ flex: 1, padding: '12px 10px' }}>
        {erreurMenu ? (
          <div style={{ padding: '20px 12px', color: C.textSec, fontSize: 13 }}>
            <div style={{ marginBottom: 10 }}>{t('sideBar.menuIndisponible')}</div>
            <button
              onClick={rechargerMenu}
              style={{
                background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
                borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12,
              }}
            >
              {t('sideBar.reessayer')}
            </button>
          </div>
        ) : chargementMenu && menu.length === 0 ? (
          <div style={{ padding: '20px 12px', color: C.textMut, fontSize: 12 }}>{t('sideBar.chargement')}</div>
        ) : (
          Object.entries(
            menu.reduce((acc, item) => {
              (acc[item.section || ''] ??= []).push(item);
              return acc;
            }, {}),
          ).map(([section, items], si) => (
            <Section
              key={section || si}
              titre={section}
              items={items}
              premiere={si === 0}
              ouverte={(sectionsOuvertes ?? []).includes(section)}
              basculer={basculerSection}
              compact={compact}
              openKeys={openKeys}
              toggleKey={toggleKey}
              isActive={isActive}
              navigate={navigate}
              nonLusMail={nonLusMail}
              nonLusConversations={nonLusConversations}
              demandesAppareils={demandesAppareils}
              declarationsCorbeille={declarationsCorbeille}
            />
          ))
        )}
      </nav>

      {/* ── Footer utilisateur + cloche ── */}
      <div style={{
        padding: '12px 14px', borderTop: `1px solid ${C.border}`, flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div
          onClick={() => navigate('/mon-profil')}
          style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, cursor: 'pointer' }}
        >
          <div style={{
            width: 34, height: 34, borderRadius: 8,
            background: 'rgba(255,255,255,0.15)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 12, flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#fff', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {username}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>
              {libelleProfil(user)}
            </div>
          </div>
        </div>

        {/* Cloche in-app - canal distinct des alertes délais et de la messagerie. */}
        <NotificationBell />
      </div>
    </aside>
  );
}